import { useState, useMemo } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Home,
  Package,
  Users,
  Truck,
  Plus,
  RefreshCw,
  FileText,
  Calendar,
  Key,
  Zap,
  Upload,
  Gift,
  Settings,
  FolderCog,
  Boxes,
  Calculator,
  FolderPlus,
  Image,
  ChevronDown,
  Briefcase,
  Link2,
  LogOut,
  UserCog,
  Mail,
  MessageSquare,
  Target,
  Brain,
  List,
  Moon,
  Sun,
  Bell,
  BellRing,
  Search,
  Menu,
  X,
  BarChart3,
  Megaphone,
  Euro,
  Landmark,
  History,
  Scale,
  RotateCcw,
  AlertTriangle,
  Wrench,
  FilePlus2,
  Receipt,
  ShoppingCart,
  Wallet,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import AlertBanner from './AlertBanner'
import { useAuth } from '@/contexts/AuthContext'

// Nueva estructura de navegacion reorganizada en grupos logicos
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home, permission: 'dashboard' },
  { name: 'Cerebro de Negocio', href: '/cerebro', icon: Brain, permission: 'brain' },
  {
    name: 'Productos',
    icon: Package,
    isGroup: true,
    permission: 'products',
    children: [
      { name: 'Listado', href: '/products/list', icon: List, permission: 'products' },
      { name: 'Crear con IA', href: '/products/create', icon: Plus, permission: 'products.create' },
      { name: 'Optimizar ficha', href: '/products/optimizer', icon: Zap, permission: 'products.optimize' },
      { name: 'Calcular precios', href: '/products/calculate-prices', icon: Calculator, permission: 'products.prices' },
      { name: 'Crear categoria', href: '/products/category/create', icon: FolderPlus, permission: 'products.categories' },
      { name: 'Optimizar categoria', href: '/products/category/optimizer', icon: Zap, permission: 'products.categories' },
      { name: 'Imagen categoria', href: '/products/category/image', icon: Image, permission: 'products.categories' },
      { name: 'Gestionar imagenes', href: '/products/images', icon: Image, permission: 'products' },
      { name: 'Catalogos Proveedores', href: '/supplier-catalogs', icon: Upload, permission: 'products.catalogs' },
      { name: 'Portal Proveedores', href: '/supplier-portal-admin', icon: Users, permission: 'products.catalogs' },
      { name: 'Etiquetas variantes', href: '/products/variants', icon: Link2, permission: 'products.variants' },
    ],
  },
  {
    name: 'Comercial',
    icon: Briefcase,
    isGroup: true,
    permission: 'commercial',
    children: [
      { name: 'Clientes', href: '/customers', icon: Users, permission: 'commercial.customers' },
      { name: 'Lista de espera', href: '/commercial/waitlist', icon: BellRing, permission: 'commercial.waitlist' },
      { name: 'SEO Analytics', href: '/seo-analytics', icon: BarChart3, permission: 'commercial.marketing' },
      { name: 'Marketing Email', href: '/marketing', icon: Mail, permission: 'commercial.marketing' },
      { name: 'Gestion de Emails', href: '/email-management', icon: Mail, permission: 'commercial.marketing' },
      { name: 'Productos Promocionales', href: '/email-promos', icon: Megaphone, permission: 'commercial.marketing' },
      { name: 'Outlet Pro (Lotes)', href: '/b2b-lots', icon: Package, permission: 'commercial.b2b_lots' },
      { name: 'WhatsApp', href: '/whatsapp', icon: MessageSquare, permission: 'commercial.marketing' },
      { name: 'Google Ads', href: '/google-ads', icon: Target, permission: 'commercial.marketing' },
      { name: 'Ads - Productos', href: '/google-ads/products', icon: Package, permission: 'commercial.marketing' },
      { name: 'Ads - Campanas', href: '/google-ads/campaigns', icon: Target, permission: 'commercial.marketing' },
      { name: 'Ads - Audiencias', href: '/google-ads/audiences', icon: Users, permission: 'commercial.marketing' },
      { name: 'Ads - Optimizador', href: '/google-ads/optimizer', icon: Brain, permission: 'commercial.marketing' },
      { name: 'Merchant Center', href: '/google-merchant', icon: ShoppingCart, permission: 'commercial.marketing' },
      { name: 'eBay', href: '/ebay', icon: Briefcase, permission: 'commercial.ebay' },
      { name: 'Facturas PDF', href: '/invoices', icon: FileText, permission: 'commercial.invoices' },
      { name: 'Factura + Envio', href: '/invoices/manual', icon: FilePlus2, permission: 'commercial.invoices' },
    ],
  },
  {
    name: 'Logistica',
    icon: Truck,
    isGroup: true,
    permission: 'logistics',
    children: [
      { name: 'Centro de Envios', href: '/shipping', icon: Package, permission: 'logistics.shipping' },
      { name: 'Devoluciones', href: '/returns-admin', icon: RotateCcw, permission: 'logistics.returns' },
      { name: 'Reparaciones', href: '/repairs-admin', icon: Wrench, permission: 'logistics.repairs' },
      { name: 'Incidencias', href: '/incidents', icon: AlertTriangle, permission: 'logistics.incidents' },
      { name: 'Costes de Envio', href: '/shipping-costs', icon: Euro, permission: 'logistics.shipping' },
      { name: 'Control de Reembolsos', href: '/reembolsos', icon: Wallet, permission: 'logistics.shipping' },
      { name: 'Reglas de Envio', href: '/shipping-rules', icon: Settings, permission: 'logistics.shipping' },
      { name: 'Configuracion Pesos', href: '/weight-config', icon: Scale, permission: 'logistics.shipping' },
      { name: 'Transportistas', href: '/carriers', icon: Truck, permission: 'logistics.carriers' },
      { name: 'Envio gratuito 2o pedido', href: '/free-shipping', icon: Gift, permission: 'logistics.freeshipping' },
    ],
  },
  {
    name: 'Integraciones',
    icon: Link2,
    isGroup: true,
    permission: 'integrations',
    children: [
      { name: 'Sincronizacion Factusol', href: '/sync', icon: RefreshCw, permission: 'integrations.sync' },
      { name: 'API Catalogo', href: '/api-clients', icon: Key, permission: 'integrations.api' },
      { name: 'Tareas Programadas', href: '/scheduler', icon: Calendar, permission: 'integrations.scheduler' },
    ],
  },
  {
    name: 'Configuracion',
    icon: Settings,
    isGroup: true,
    permission: 'configuration',
    children: [
      { name: 'Familias', href: '/configuration/families', icon: FolderCog, permission: 'configuration.families' },
      { name: 'Proveedores', href: '/configuration/suppliers', icon: Truck, permission: 'configuration.suppliers' },
      { name: 'Stock Minimo', href: '/configuration/stock-minimum', icon: Boxes, permission: 'configuration.stock' },
    ],
  },
  {
    name: 'Informes',
    icon: BarChart3,
    isGroup: true,
    permission: 'reports',
    children: [
      { name: 'Beneficios', href: '/reports/profit', icon: Euro, permission: 'reports.profit' },
      { name: 'Beneficio Neto', href: '/reports/net-profit', icon: Wallet, permission: 'reports.profit' },
      { name: 'Gastos', href: '/reports/expenses', icon: Receipt, permission: 'reports.profit' },
      { name: 'Valor del negocio', href: '/reports/valor', icon: Landmark, permission: 'reports.profit' },
      { name: 'Transportistas', href: '/reports/transporte', icon: Truck, permission: 'reports.profit' },
      { name: 'Asesoría (CSV)', href: '/reports/asesoria', icon: FileText, permission: 'reports.profit' },
      { name: 'Pedidos a proveedores', href: '/reports/supplier-orders', icon: Package, permission: 'reports.supplier_orders' },
      { name: 'Historial de Precios', href: '/price-history', icon: History, permission: 'products.prices' },
    ],
  },
  {
    name: 'Administracion',
    icon: UserCog,
    isGroup: true,
    permission: 'admin',
    children: [{ name: 'Usuarios', href: '/users', icon: Users, permission: 'admin.users' }],
  },
]

type NavChild = {
  name: string
  href: string
  icon: any
  permission: string
}

type NavItem = {
  name: string
  href?: string
  icon: any
  permission: string
  isGroup?: boolean
  children?: NavChild[]
}

// Componente para grupo de navegacion colapsable
function NavGroup({
  item,
  isExpanded,
  onToggle,
  location,
  hasPermission,
}: {
  item: NavItem
  isExpanded: boolean
  onToggle: () => void
  location: { pathname: string }
  hasPermission: (section: string) => boolean
}) {
  const Icon = item.icon

  // Filtrar children según permisos
  const visibleChildren = item.children?.filter((child) => hasPermission(child.permission)) || []

  // Si no hay children visibles, no mostrar el grupo
  if (visibleChildren.length === 0) {
    return null
  }

  const hasActiveChild = visibleChildren.some((child) => location.pathname === child.href)

  return (
    <div className="space-y-0.5">
      <button
        onClick={onToggle}
        className={cn(
          'nav-item w-full justify-between group',
          hasActiveChild && 'nav-item-active'
        )}
        aria-expanded={isExpanded}
      >
        <span className="flex items-center gap-3">
          <span
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-lg transition-colors',
              hasActiveChild
                ? 'bg-primary-100 dark:bg-primary-900/40'
                : 'bg-slate-100 dark:bg-slate-700/50 group-hover:bg-slate-200 dark:group-hover:bg-slate-700'
            )}
          >
            <Icon
              className={cn(
                'w-4 h-4 transition-colors',
                hasActiveChild ? 'text-primary-600 dark:text-primary-400' : 'text-slate-500 dark:text-slate-400'
              )}
              aria-hidden="true"
            />
          </span>
          <span className="font-medium">{item.name}</span>
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-slate-400 transition-transform duration-200',
            isExpanded && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>

      {/* Submenu con animacion */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-200 ease-in-out',
          isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="ml-4 pl-4 border-l-2 border-slate-100 dark:border-slate-700 space-y-0.5 py-1">
          {visibleChildren.map((child) => {
            const ChildIcon = child.icon
            const isChildActive = location.pathname === child.href
            return (
              <Link
                key={child.name}
                to={child.href}
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all duration-150',
                  isChildActive
                    ? 'bg-primary-600 text-white shadow-sm shadow-primary-600/25'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white'
                )}
              >
                <ChildIcon className="w-4 h-4" aria-hidden="true" />
                <span>{child.name}</span>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, hasPermission } = useAuth()
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark')
    }
    return false
  })
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Estado para controlar que grupos estan expandidos
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    // Inicialmente expandir el grupo que contiene la ruta activa
    const expanded = new Set<string>()
    navigation.forEach((item) => {
      if (
        item.isGroup &&
        item.children?.some((child) =>
          location.pathname.startsWith(child.href.split('/').slice(0, 2).join('/'))
        )
      ) {
        expanded.add(item.name)
      }
    })
    return expanded
  })

  const toggleGroup = (name: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })
  }

  const toggleDarkMode = () => {
    const html = document.documentElement
    html.classList.add('theme-transitioning')
    if (isDarkMode) {
      html.classList.remove('dark')
    } else {
      html.classList.add('dark')
    }
    setIsDarkMode(!isDarkMode)
    setTimeout(() => html.classList.remove('theme-transitioning'), 100)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Filtrar navegación según permisos
  const filteredNavigation = useMemo(() => {
    return navigation.filter((item) => {
      if (item.isGroup) {
        // Un grupo es visible si tiene al menos un hijo visible
        return item.children?.some((child) => hasPermission(child.permission))
      }
      return hasPermission(item.permission)
    })
  }, [hasPermission])

  // Obtener iniciales del usuario
  const userInitials = useMemo(() => {
    if (user?.full_name) {
      return user.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    return user?.username?.slice(0, 2).toUpperCase() || 'U'
  }, [user])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Sidebar */}
      <aside
        className={cn(
          'sidebar',
          isMobileMenuOpen && 'translate-x-0'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="sidebar-header">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary-600 to-accent-600 shadow-lg shadow-primary-600/20">
                <span className="text-xl font-bold text-white">J</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900 dark:text-white">JVS</h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 -mt-0.5">Informatica</p>
              </div>
            </Link>
            {/* Mobile close button */}
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="sidebar-nav scrollbar-hide" aria-label="Menu principal">
            {filteredNavigation.map((item) => {
              // Si es un grupo, renderizar NavGroup
              if (item.isGroup) {
                return (
                  <NavGroup
                    key={item.name}
                    item={item as NavItem}
                    isExpanded={expandedGroups.has(item.name)}
                    onToggle={() => toggleGroup(item.name)}
                    location={location}
                    hasPermission={hasPermission}
                  />
                )
              }

              // Item simple (Dashboard)
              const Icon = item.icon
              const isActive = location.pathname === item.href

              return (
                <Link
                  key={item.name}
                  to={item.href!}
                  className={cn('nav-item group', isActive && 'nav-item-active')}
                >
                  <span
                    className={cn(
                      'flex items-center justify-center w-8 h-8 rounded-lg transition-colors',
                      isActive
                        ? 'bg-primary-100 dark:bg-primary-900/40'
                        : 'bg-slate-100 dark:bg-slate-700/50 group-hover:bg-slate-200 dark:group-hover:bg-slate-700'
                    )}
                  >
                    <Icon
                      className={cn(
                        'w-4 h-4 transition-colors',
                        isActive ? 'text-primary-600 dark:text-primary-400' : 'text-slate-500 dark:text-slate-400'
                      )}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="font-medium">{item.name}</span>
                </Link>
              )
            })}
          </nav>

          {/* Footer con usuario y logout */}
          <div className="sidebar-footer space-y-3">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
            >
              {isDarkMode ? (
                <>
                  <Sun className="w-5 h-5 text-amber-500" />
                  <span>Modo claro</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5 text-slate-500" />
                  <span>Modo oscuro</span>
                </>
              )}
            </button>

            {/* User info */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="avatar avatar-md bg-gradient-to-br from-primary-500 to-accent-500 text-white font-semibold">
                  {userInitials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                    {user?.full_name || user?.username}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                </div>
              </div>
              {user?.is_admin && (
                <div className="mt-2">
                  <span className="badge badge-primary text-2xs">Administrador</span>
                </div>
              )}
            </div>

            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-xl transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Cerrar sesion</span>
            </button>

            <p className="px-4 text-2xs text-slate-400 dark:text-slate-500">v2.1.0 - FastAPI + React</p>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="main-content">
        {/* Top bar */}
        <header className="topbar">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <Menu className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>

            {/* Search bar */}
            <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700/50 rounded-xl w-80">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar..."
                className="flex-1 bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
              />
              <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 text-xs text-slate-400 bg-white dark:bg-slate-600 rounded border border-slate-200 dark:border-slate-500">
                ⌘K
              </kbd>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <button className="relative p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <Bell className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-danger-500 rounded-full"></span>
            </button>

            {/* User avatar - desktop */}
            <div className="hidden lg:flex items-center gap-3 pl-3 border-l border-slate-200 dark:border-slate-700">
              <div className="avatar avatar-sm bg-gradient-to-br from-primary-500 to-accent-500 text-white text-xs font-semibold">
                {userInitials}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="content-container animate-fade-in">
          <AlertBanner />
          <Outlet />
        </div>
      </main>
    </div>
  )
}
